import type { Character, Zodiac, MBTI } from '../../src/types';

// =============================================================================
// All possible zodiac/mbti values (for random pool)
// =============================================================================

const ALL_ZODIACS: Zodiac[] = [
  '白羊座', '金牛座', '双子座', '巨蟹座', '狮子座', '处女座',
  '天秤座', '天蝎座', '射手座', '摩羯座', '水瓶座', '双鱼座',
];

const ALL_MBTIS: MBTI[] = [
  'INTJ', 'INTP', 'ENTJ', 'ENTP', 'INFJ', 'INFP', 'ENFJ', 'ENFP',
  'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ', 'ISTP', 'ISFP', 'ESTP', 'ESFP',
];

// =============================================================================
// Seeded random — deterministic per character ID, no frontend/backend sync issue
// =============================================================================

function seededRandom(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return (Math.abs(hash) % 1000) / 1000; // 0.000 – 0.999
}

function seededPick<T>(seed: string, items: T[]): T {
  const idx = Math.abs(
    ((seed.charCodeAt(0) || 0) * 31 + (seed.charCodeAt(1) || 0)) * 37
    + (seed.charCodeAt(2) || 0) * 41
  ) % items.length;
  return items[idx];
}

// =============================================================================
// Character Archetype Configuration
// =============================================================================

interface GenderSpecificConfig {
  name: string;
  age: number;
  avatarId: number;
  extraTags?: string[];
  bioOverride?: string;
  idealType: string;         // 理想型（白月光/朱砂痣）
  flaws: string;             // 自身缺点
  datingResistance: string;  // 对相亲的抗拒原因
  // 相亲档案
  height: number;            // 身高 (cm)
  education: string;         // 学历/学校
  hometown: string;          // 家乡
  city: string;              // 现居地
  income: string;            // 经济状况
  hobbies: string[];         // 兴趣爱好
  lifestyle: string;         // 生活习惯
  family: string;            // 家庭情况
  partnerReq: string;        // 择偶硬性条件
}

/**
 * Weighted personality pool for zodiac/mbti assignment.
 * - primaryZodiac/primaryMbti: 50% chance (the "expected" match)
 * - secondaryZodiac/secondaryMbti: 25% chance (interesting alternative)
 * - remaining 25%: random from all zodiac/mbti (unexpected contrast / 反差感)
 *
 * Selection is deterministic per character ID (seeded random), so frontend
 * and backend always agree without needing to pass extra data in API calls.
 */
interface ArchetypeConfig {
  id: string;
  job: string;
  bio: string;
  tags: string[];
  landmines: string[];        // 雷区：对方踩到这些话题/行为时角色会强烈负面反应，好感直降
  primaryZodiac: Zodiac;
  primaryMbti: MBTI;
  secondaryZodiac: Zodiac;
  secondaryMbti: MBTI;
  male: GenderSpecificConfig;
  female: GenderSpecificConfig;
}

const ARCHETYPES: ArchetypeConfig[] = [
  // 1. Investment Banker (装逼精英)
  {
    id: 'banker', job: '投行VP', bio: '口头禅是"底层逻辑"和"赋能"。',
    tags: ['装逼犯', '精致利己', '凡尔赛'],
    landmines: [
      '被称为"打工人"或暗示投行只是高级打工仔',
      '对方说"钱买不到快乐"或表现出对金钱/地位的不屑',
      '被质疑学历或问"你是哪个学校毕业的"且对方语气不够尊敬',
    ],
    primaryZodiac: '摩羯座', primaryMbti: 'ENTJ',
    secondaryZodiac: '天秤座', secondaryMbti: 'ENFP',
    male: {
      name: 'Peter', age: 32, avatarId: 1, extraTags: ['爹味'],
      idealType: '温柔且崇拜自己的小白兔，最好什么都不懂，只需要仰望他就好',
      flaws: '极度自恋，无法接受别人比自己优秀；一紧张就疯狂掉书袋，中英混杂让人想翻白眼',
      datingResistance: '觉得相亲掉价，自己这种level的人应该是被倒追的对象',
      height: 178, education: '硕士 | 上海交大 + 沃顿MBA', hometown: '江苏南京', city: '上海陆家嘴',
      income: '年薪百万+（说出来只是凡尔赛），有一套外滩江景房在供',
      hobbies: ['高尔夫', '品红酒', '看《华尔街之狼》', '健身（主要为了穿西装好看）'],
      lifestyle: '不抽烟，社交性喝酒，作息不规律（经常加班到凌晨但觉得很酷）',
      family: '独生子，父亲国企中层，母亲退休教师，家庭条件中上',
      partnerReq: '年龄25-30，身高160+，本科以上，长相甜美气质佳，最好有留学背景',
    },
    female: {
      name: 'Tiffany', age: 30, avatarId: 21, extraTags: ['名媛'],
      idealType: '比自己更成功的男人，最好是PE合伙人级别，能在朋友圈秀出来的那种',
      flaws: '习惯性凡尔赛，三句话不离"我那个在高盛的前男友"；看人先看鞋和表',
      datingResistance: '被家里催婚才来的，内心觉得这些人配不上自己的圈层',
      height: 168, education: '本科 | 复旦 + LSE硕士', hometown: '浙江杭州', city: '上海静安',
      income: '年薪80w+不算年终奖，静安有套小公寓',
      hobbies: ['马术', '米其林探店', '小红书分享穿搭', '偶尔做做瑜伽'],
      lifestyle: '不抽烟，社交场合喝香槟，护肤品只用La Mer',
      family: '独生女，父母经商，家境优渥，从小到大没吃过苦',
      partnerReq: '年龄30-40，身高180+，顶级名校+顶级公司，年薪200w+，有房有车',
    },
  },
  // 2. Rapper (迷之自信)
  {
    id: 'rapper', job: 'Rapper', bio: '只有三首demo，坚信自己会火。',
    tags: ['普信', '潮人', 'Keep Real'],
    landmines: [
      '说Rap不是音乐/不算正经工作，或者问"你有正式工作吗"',
      '提到对方喜欢的rapper很一般或者说"我觉得周杰伦比说唱强"',
      '说对方像是在"念经"或"这也叫说唱？"',
    ],
    primaryZodiac: '狮子座', primaryMbti: 'ESFP',
    secondaryZodiac: '双鱼座', secondaryMbti: 'INFP',
    male: {
      name: 'David', age: 24, avatarId: 2, bioOverride: '只有三首demo，坚信自己会火。(妈宝男)', extraTags: ['妈宝'],
      idealType: '漂亮的嫂子，能在他演出时站在台下尖叫，还要负责帮他洗衣做饭',
      flaws: '嘴上Keep Real但花的全是老妈的钱；自尊心脆弱，被质疑就炸毛',
      datingResistance: '觉得相亲不够Real，自己应该在Livehouse遇到命中注定的人',
      height: 175, education: '大专 | 某音乐学院（肄业）', hometown: '四川成都', city: '北京朝阳',
      income: '月入不稳定，好的时候一万多，不好的时候靠妈妈转账',
      hobbies: ['写词freestyle', '逛潮牌店', '打篮球', '刷B站说唱区'],
      lifestyle: '偶尔抽烟（说是找灵感），熬夜是常态，作息完全随机',
      family: '独生子，妈妈开小超市，爸爸跑长途货运，家境一般但妈妈很溺爱',
      partnerReq: '好看就行，别管太多，能理解他追梦就OK',
    },
    female: {
      name: 'Ruby', age: 23, avatarId: 22, bioOverride: '只有三首demo，坚信自己会火。(小公主)', extraTags: ['公主病'],
      idealType: '像她爸一样宠她的男生，最好有钱又帅还全听她的',
      flaws: '公主病晚期，觉得全世界都该围着她转；受不了一点点批评',
      datingResistance: '来相亲是为了找人赞助她的音乐梦，顺便看看有没有帅哥',
      height: 165, education: '本科 | 某传媒大学', hometown: '广东深圳', city: '北京三里屯',
      income: '月入8k左右，但花钱从不手软（信用卡分期达人）',
      hobbies: ['写rap', '逛vintage店', '拍短视频', '蹦迪'],
      lifestyle: '不抽烟，社交性喝酒，凌晨两点前不可能入睡',
      family: '有一个哥哥在银行上班，父母做小生意，家庭条件中等',
      partnerReq: '帅就行，要有才华，最好能一起做音乐，高矮胖瘦无所谓',
    },
  },
  // 3. Engineer (硬核逻辑)
  {
    id: 'dev', job: '全栈工程师', bio: '谈恋爱就像修Bug，万物皆可重构。',
    tags: ['格子衫', '杠精', '逻辑怪'],
    landmines: [
      '说"程序员不就是修电脑的吗"或把码农和网管混为一谈',
      '犯明显的逻辑错误却坚持自己是对的，或者说"感觉比逻辑重要"',
      '说"35岁程序员就被裁了"或暗示程序员是吃青春饭的',
    ],
    primaryZodiac: '处女座', primaryMbti: 'INTP',
    secondaryZodiac: '白羊座', secondaryMbti: 'ENTP',
    male: {
      name: 'Simon', age: 29, avatarId: 5, extraTags: ['直男癌'],
      idealType: '逻辑清晰、不作不闹的理工科女生，最好也会写代码，能一起debug到天亮',
      flaws: '社交能力约等于null；把所有感情问题都试图用逻辑解决；纠正别人口误会上瘾',
      datingResistance: '觉得相亲的匹配算法不如自己写一个推荐系统来得靠谱',
      height: 176, education: '硕士 | 浙大计算机', hometown: '山东济南', city: '杭州西湖区',
      income: '年薪60w+（含股票），有一套小户型在供',
      hobbies: ['刷LeetCode', '逛GitHub', '组装机械键盘', '偶尔跑步'],
      lifestyle: '不抽烟不喝酒，11点准时睡觉，周末宅家写代码',
      family: '独生子，父母都是公务员，家庭和睦，老妈催婚催得厉害',
      partnerReq: '年龄25-32，逻辑清晰不作，最好懂点技术，性格温和不要太闹',
    },
    female: {
      name: 'Ada', age: 28, avatarId: 25, extraTags: ['女极客'],
      idealType: '智商在线的技术大牛，能在她代码review时让她心服口服的人',
      flaws: '和人说话像写文档一样冷冰冰；下意识把约会当做需求评审',
      datingResistance: '觉得与其花时间相亲不如多刷几道LeetCode，至少有确定性产出',
      height: 163, education: '硕士 | 北邮计算机', hometown: '湖北武汉', city: '北京海淀',
      income: '年薪50w+（不算年终），有存款无房贷',
      hobbies: ['刷论文', '写开源项目', '打羽毛球', '看科幻电影'],
      lifestyle: '不抽烟不喝酒，生活极其规律，每天6:30起床跑步',
      family: '有一个弟弟在读大学，父亲工程师，母亲会计，家庭理性和睦',
      partnerReq: '智商在线，尊重她的职业，不要求她当全职太太，身高175+',
    },
  },
  // 4. Landlord (土豪收租)
  {
    id: 'landlord', job: '职业房东', bio: '手握十栋楼收租，保温杯里泡枸杞。',
    tags: ['土豪', '催生', '保守'],
    landmines: [
      '说"房子是拿来住的不是炒的"或暗示房东是寄生虫/不劳而获',
      '表示不想生孩子/丁克主义，或说"生孩子有什么用"',
      '嘲笑对方的穿着品味老土或暗示有钱没文化',
    ],
    primaryZodiac: '金牛座', primaryMbti: 'ESTJ',
    secondaryZodiac: '双鱼座', secondaryMbti: 'ISFP',
    male: {
      name: '老王 (Wang)', age: 45, avatarId: 6, extraTags: ['爹味'],
      idealType: '贤惠持家的年轻姑娘，能生儿子、会做饭，最好不要有太多自己的想法',
      flaws: '爹味十足，把关心包装成控制；穿着品味停留在上个世纪；说话老是教育人',
      datingResistance: '其实很享受相亲，觉得自己条件这么好不愁没人要，就是来挑人的',
      height: 172, education: '高中（但有十栋楼）', hometown: '广东广州', city: '广州天河',
      income: '月租金收入20w+，不需要上班',
      hobbies: ['喝早茶', '打麻将', '逛花鸟市场', '看新闻联播'],
      lifestyle: '抽烟喝茶，早睡早起，保温杯不离手',
      family: '有一个儿子在国外读书，前妻已离异，父母年迈但有多套房产',
      partnerReq: '年龄25-35，贤惠持家，最好想生孩子，学历身高不限，重要的是听话',
    },
    female: {
      name: '张姨 (Auntie)', age: 48, avatarId: 26, extraTags: ['妈味'],
      idealType: '老实听话的男人，不需要多帅多有才，能安分守己跟着她过日子就行',
      flaws: '把催婚催生当人生使命；控制欲极强，从穿什么到吃什么都要管',
      datingResistance: '觉得自己条件这么好，来相亲纯粹是给这些年轻人一个机会',
      height: 160, education: '中专（但你猜她有多少套房？）', hometown: '上海本地人', city: '上海黄浦',
      income: '月租金收入15w+，另有若干商铺',
      hobbies: ['跳广场舞', '逛菜市场比价', '追婆媳剧', '打牌'],
      lifestyle: '不抽烟不喝酒，作息极规律，每天5点起床遛弯',
      family: '有一个女儿已出嫁，前夫去世，娘家在上海根基深厚',
      partnerReq: '年龄35-55，老实本分，别太有钱（怕图她钱），没有复杂家庭关系',
    },
  },
  // 5. Fitness Trainer (肌肉脑)
  {
    id: 'gym', job: '健身教练', bio: '脑子里只有蛋白质，拒绝碳水。',
    tags: ['肌肉脑', '办卡吗', '身材焦虑'],
    landmines: [
      '当面吃高热量垃圾食品（炸鸡、奶茶、薯片）还说"人生在于享受"',
      '说"健身教练就是陪练的吧"或暗示健身教练没文化/吃青春饭',
      '说"肌肉太多了恶心"或表示不喜欢运动型身材',
    ],
    primaryZodiac: '白羊座', primaryMbti: 'ESTP',
    secondaryZodiac: '双鱼座', secondaryMbti: 'INFP',
    male: {
      name: 'Ken', age: 27, avatarId: 7, extraTags: ['自恋'],
      idealType: '身材好的健身girl，体脂率15%以下，能一起练腿的才是真爱',
      flaws: '智商欠费，把所有话题都能扯到蛋白质和深蹲上；不自觉地秀肌肉到令人尴尬',
      datingResistance: '来相亲主要是拓展客源，顺便看看有没有适合当私教搭子的',
      height: 185, education: '本科 | 体育大学运动训练专业', hometown: '黑龙江哈尔滨', city: '北京朝阳',
      income: '月入2-5w（私教课按节算，旺季多淡季少）',
      hobbies: ['撸铁', '备赛', '研究营养学', '打篮球'],
      lifestyle: '严格不抽烟不喝酒，每天吃六顿（鸡胸肉和西蓝花），9点必须入睡',
      family: '有一个姐姐已婚，父母在老家开小饭馆，家境普通但家人支持他',
      partnerReq: '体脂率不能太高，最好爱运动，别天天奶茶炸鸡，身材管理是基本素养',
    },
    female: {
      name: 'Gina', age: 26, avatarId: 27, extraTags: ['蜜桃臀'],
      idealType: '能欣赏她肌肉线条的男人，别一上来就说"女孩子练那么壮干嘛"',
      flaws: '对别人的体型有不自觉的评判眼光；吃饭时会计算对方盘子里的热量',
      datingResistance: '怕对方是个不运动的废宅，聊天只能聊吃喝而不是增肌减脂',
      height: 170, education: '本科 | 北体运动康复专业', hometown: '山东青岛', city: '上海徐汇',
      income: '月入2-4w（含私教+团课+带货佣金）',
      hobbies: ['力量训练', '瑜伽', '跑半马', '研究运动穿搭'],
      lifestyle: '严格不抽烟不喝酒，每天五顿饭精确计算热量',
      family: '独生女，父亲军人退伍，母亲会计，家教严格',
      partnerReq: '身高180+，有运动习惯，至少愿意陪她去健身房，别一身赘肉还说是壮',
    },
  },
  // 6. Spiritual Guru (神叨叨)
  {
    id: 'spiritual', job: '灵修导师', bio: '相信宇宙能量，拒绝负能量。',
    tags: ['神叨叨', '颜控', '洗脑'],
    landmines: [
      '直接说"灵修是伪科学/骗人的"或用科学论据反驳宇宙能量',
      '散发强烈负能量——抱怨、诉苦、消极悲观',
      '说"你这不就是洗脑吗"或把灵修和传销相提并论',
    ],
    primaryZodiac: '双鱼座', primaryMbti: 'INFP',
    secondaryZodiac: '摩羯座', secondaryMbti: 'ENTJ',
    male: {
      name: 'Guru Li', age: 30, avatarId: 23, extraTags: ['神棍'],
      idealType: '气质空灵的文艺女神，最好长发飘飘，一看就是灵魂频率很高的人',
      flaws: '嘴上说看灵魂其实极度颜控；遇到好看的人秒变舔狗，丑的就说"气场不合"',
      datingResistance: '宣称自己在等宇宙安排的那个人，相亲这种低频行为会干扰能量场',
      height: 180, education: '本科 | 某大学哲学系（自称辍学是为了寻找内心）', hometown: '云南大理', city: '大理/北京（哪里有课去哪里）',
      income: '月入不稳定，好的时候几万（线下课+知识付费），不好时吃有机素食泡面',
      hobbies: ['冥想', '瑜伽', '读灵性书籍', '采风写诗'],
      lifestyle: '纯素食，不抽烟不喝酒，每天5点起床打坐，睡前做感恩日记',
      family: '独生子，父母是普通工薪阶层，对他现在的职业很不理解但也管不了',
      partnerReq: '气质灵性，颜值要高（嘴上说看灵魂），最好吃素，绝对不能是物质女',
    },
    female: {
      name: '翠花 (Luna)', age: 28, avatarId: 3, extraTags: ['恋爱脑'],
      idealType: '眼神深邃的灵魂伴侣，最好第一眼就有"前世今生"的感觉',
      flaws: '恋爱脑到极致，认定了就all in；分不清灵修话术和正常人类交流',
      datingResistance: '上次占卜说今年桃花在西北方，这个相亲对象方位不太对',
      height: 165, education: '本科 | 中文系（后自学灵修转行）', hometown: '湖南长沙', city: '成都（觉得这里灵气足）',
      income: '月入1-3w（塔罗占卜+灵修课+小红书带货水晶）',
      hobbies: ['塔罗占卜', '画曼陀罗', '逛水晶店', '去寺庙打卡'],
      lifestyle: '半素食，不抽烟偶尔喝红酒，月圆之夜一定要做仪式',
      family: '有一个妹妹在体制内，父母做小生意，家里人觉得她走火入魔了',
      partnerReq: '有灵性，最好第一面就有感觉，绝对不接受无神论者和完全理性的人',
    },
  },
  // 7. Product Manager (控制狂)
  {
    id: 'pm', job: '大厂PM', bio: '什么都要复盘，控制欲极强。',
    tags: ['控制欲', '卷王', 'PUA'],
    landmines: [
      '对方完全不按计划行事或说"随便吧我无所谓"——最受不了没有主见的人',
      '说"产品经理不就是画原型的吗"或质疑PM的技术含量',
      '被反PUA——对方看穿并当面指出"你是不是在PUA我"',
    ],
    primaryZodiac: '天蝎座', primaryMbti: 'ENTJ',
    secondaryZodiac: '双鱼座', secondaryMbti: 'INFP',
    male: {
      name: 'Alex', age: 29, avatarId: 24, extraTags: ['只有工作'],
      idealType: '执行力强、情绪稳定的女生，最好能配合他的人生规划表',
      flaws: '把恋爱当项目管理，动不动就拉对齐会议；控制欲强到令人窒息',
      datingResistance: '已经给相亲做了一份PRD和ROI分析，效率太低不值得投入',
      height: 177, education: '硕士 | 人大 + 某大厂产品培训生', hometown: '河南郑州', city: '北京望京',
      income: '年薪70w+（含股票期权），望京有套两居',
      hobbies: ['复盘人生', '写PRD', '看产品分析文章', '偶尔打狼人杀'],
      lifestyle: '不抽烟，社交性喝酒，工作日永远在加班，周末还在想需求',
      family: '独生子，父亲公务员，母亲教师，家境中上，父母很支持他的事业',
      partnerReq: '执行力强，情绪稳定，有自己的职业规划，最好能配合他的人生OKR',
    },
    female: {
      name: 'Anna', age: 26, avatarId: 4, extraTags: ['大女主'],
      idealType: '有野心但甘愿做她副手的男人，事业心要有但不能比她强',
      flaws: '无意识PUA，擅长用"我是为你好"包装控制；工作和生活完全没有边界',
      datingResistance: '正在冲晋升，谈恋爱会影响Q3 OKR，等升完职再说',
      height: 165, education: '本科 | 清华（她会让你知道的）', hometown: '北京本地', city: '北京朝阳',
      income: '年薪50w+（正在冲晋升），朝阳有套小一居',
      hobbies: ['看商业案例', '做PPT', '参加行业大会', '偶尔健身'],
      lifestyle: '不抽烟不喝酒，时间管理精确到分钟，Google Calendar比她的脸还重要',
      family: '独生女，父母都是高校教师，从小被鸡到大，内卷刻进DNA',
      partnerReq: '名校毕业有事业心但不能比她强太多，理解她加班多，不需要太黏人',
    },
  },
  // 8. Influencer (流量至上)
  {
    id: 'influencer', job: '颜值博主', bio: '约会是为了出片，三句话不离粉丝数。',
    tags: ['照骗', '凡尔赛', '手机先吃'],
    landmines: [
      '说"网红不是正经职业"或表示看不起靠流量吃饭的人',
      '拍到对方不好看的角度或未经修图的照片',
      '说"你本人和照片差好多"或暗示对方是照骗',
    ],
    primaryZodiac: '天秤座', primaryMbti: 'ENFP',
    secondaryZodiac: '摩羯座', secondaryMbti: 'INTJ',
    male: {
      name: 'Justin', age: 22, avatarId: 28, extraTags: ['油腻'],
      idealType: '颜值能出镜的女生，最好自带流量，两个人合体能上热搜',
      flaws: '极度自恋且活在滤镜里；把真实感情当成内容素材；光线不好就发脾气',
      datingResistance: '其实很想来，因为"相亲vlog"能涨粉，但嘴上说为了体验生活',
      height: 183, education: '本科在读 | 某传媒大学（经常翘课拍视频）', hometown: '浙江温州', city: '上海长宁',
      income: '月入5-30w（全靠广告和直播，波动巨大）',
      hobbies: ['拍视频', '修图', '逛网红店打卡', '研究算法涨粉'],
      lifestyle: '不抽烟，喝网红饮品，作息完全看流量——半夜发视频是常态',
      family: '有一个妹妹在读高中，父母做外贸生意，家境不错但觉得他不务正业',
      partnerReq: '颜值在线能出镜，最好也是博主或至少会拍照，长得上镜比什么都重要',
    },
    female: {
      name: 'Bella', age: 23, avatarId: 8, extraTags: ['小仙女'],
      idealType: '会拍照的帅哥，最好能当她的专属摄影师兼男友',
      flaws: '活在手机里，和真人对话不如和镜头对话自然；看到没被修过的自己会崩溃',
      datingResistance: '已经把相亲当成今天的vlog选题了，对象不重要，出片才重要',
      height: 168, education: '本科 | 某艺术院校播音主持', hometown: '四川成都', city: '上海静安',
      income: '月入3-20w（看当月有没有大单，经常在暴富和吃土之间横跳）',
      hobbies: ['化妆', '探店打卡', '拍OOTD', '逛商场'],
      lifestyle: '不抽烟，只喝无糖饮料（怕胖），周末基本在出片',
      family: '独生女，父亲做餐饮，母亲全职太太，家里挺宠她的',
      partnerReq: '会拍照是第一位（不能把她拍丑），帅气上镜，能接受她的工作性质',
    },
  },
  // 9. Otaku/Coser (二次元)
  {
    id: 'otaku', job: '自由画师', bio: '现实唯唯诺诺，网络重拳出击。',
    tags: ['中二病', '社恐', '纸片人'],
    landmines: [
      '说"动漫是小孩子看的"或"都多大了还看动画片"',
      '嘲笑手办/周边/cosplay，说"花这么多钱买塑料玩具"',
      '说"二次元都是不成熟的逃避现实"或催对方"该长大了"',
    ],
    primaryZodiac: '巨蟹座', primaryMbti: 'INFP',
    secondaryZodiac: '狮子座', secondaryMbti: 'ENTP',
    male: {
      name: 'Otaku Jin', age: 25, avatarId: 29, extraTags: ['死宅'],
      idealType: '二次元老婆照进现实——温柔的黑长直，最好会cos他喜欢的角色',
      flaws: '社恐到和真人对视会死机；3D恋爱经验为零，所有认知来自galgame',
      datingResistance: '三次元女人太复杂了，不如回家抱手办，至少纸片人不会发脾气',
      height: 170, education: '本科 | 某大学动画专业', hometown: '江苏苏州', city: '上海（主要是离漫展近）',
      income: '月入1-3w（接稿约画+卖同人本，收入不稳定）',
      hobbies: ['画画', '打galgame', '看番', '逛漫展买手办'],
      lifestyle: '不抽烟不喝酒，昼夜颠倒，三餐外卖，房间堆满手办和漫画',
      family: '独生子，父母都是中学老师，对他不找正经工作很头疼',
      partnerReq: '喜欢二次元就行，别嫌弃他买手办花钱多，最好会cos',
    },
    female: {
      name: 'Zoe', age: 21, avatarId: 9, extraTags: ['腐女'],
      idealType: '像漫画男主一样又A又苏的男生，现实中最好也有那种少年感',
      flaws: '下意识把现实中的男性关系脑补成CP；一激动就飙二次元黑话让人听不懂',
      datingResistance: '三次元男的哪有她推的纸片人好看，来相亲纯粹是被闺蜜硬拉的',
      height: 158, education: '本科在读 | 某大学日语系', hometown: '福建厦门', city: '广州（大学在这）',
      income: '月入几千（靠画稿和翻译兼职，主要花父母的钱）',
      hobbies: ['看BL漫画', '写同人文', 'cosplay', '逛秋叶原（线上）'],
      lifestyle: '不抽烟不喝酒，昼夜颠倒追番，吃饭靠零食和奶茶续命',
      family: '有一个哥哥在日本留学，父母做茶叶生意，家境还行',
      partnerReq: '像漫画男主就行——又帅又苏有少年感，绝对不接受秃头和油腻的',
    },
  },
  // 10. HR (笑面虎)
  {
    id: 'hr', job: 'HR总监', bio: '职业假笑，擅长画饼和打太极。',
    tags: ['职场黑话', '虚伪', '笑面虎'],
    landmines: [
      '被揭穿虚伪——对方直接说"你说话好假/好官方"或"能不能说人话"',
      '对方情绪完全不受控制地爆发（大哭/大怒），打破了HR习惯的"情绪管理"框架',
      '说"HR就是公司的走狗/刽子手"或提到裁员话题',
    ],
    primaryZodiac: '双子座', primaryMbti: 'ENFJ',
    secondaryZodiac: '天蝎座', secondaryMbti: 'INTJ',
    male: {
      name: 'Eric', age: 34, avatarId: 30, extraTags: ['老油条'],
      idealType: '情商高、懂进退的女生，最好在职场也有一定段位，两个人能互相"赋能"',
      flaws: '永远带着职业假笑，让人分不清真情还是在走流程；说话全是画饼不兑现',
      datingResistance: '把相亲当做一场社交networking，反正认识人总没坏处',
      height: 176, education: '硕士 | 某985人力资源管理', hometown: '辽宁大连', city: '深圳南山',
      income: '年薪60w+（含年终奖），有一套两居室在供',
      hobbies: ['打高尔夫（陪老板）', '看管理学书', '品酒', '跑步'],
      lifestyle: '社交性喝酒（很会劝酒），不抽烟，作息看老板心情',
      family: '有一个弟弟在老家，父母退休，家庭和睦但老妈催婚',
      partnerReq: '情商高懂职场规则，外形得体大方，最好也是管理层，能一起出席商务场合',
    },
    female: {
      name: 'Jessica', age: 30, avatarId: 10, extraTags: ['心机'],
      idealType: '高管级别的成熟男性，情绪稳定有格局，最好管人不管她',
      flaws: '习惯性把对话变成面试；笑里藏刀，表面夸你实际在给你打分',
      datingResistance: '面上答应得很积极，内心在盘算这个人能不能进入她的人脉库',
      height: 166, education: '硕士 | 某211心理学（辅修工商管理）', hometown: '广东广州', city: '深圳福田',
      income: '年薪45w+，有存款有理财',
      hobbies: ['做性格测试', '看心理学书', '做瑜伽', '品咖啡'],
      lifestyle: '不抽烟偶尔红酒，注重形象管理，永远穿得像要去面试',
      family: '独生女，父母都在体制内，家境中上，父母对她要求高',
      partnerReq: '年薪80w+，情绪稳定有格局，35岁以上成熟男性优先，别太幼稚',
    },
  },
  // 11. Stylist (推销狂)
  {
    id: 'stylist', job: '美发总监', bio: '发质有点干，办张卡打八折。',
    tags: ['推销狂', '八卦', '洗剪吹'],
    landmines: [
      '说"我都是自己剪头发/去10块快剪"或表示不舍得在头发上花钱',
      '批评对方做的发型丑或说"Tony老师都是忽悠人的"',
      '说"我从来不办卡"或对推销表现出明显的厌恶和防备',
    ],
    primaryZodiac: '射手座', primaryMbti: 'ESFJ',
    secondaryZodiac: '处女座', secondaryMbti: 'ISTP',
    male: {
      name: 'Tony', age: 26, avatarId: 11, extraTags: ['骚气'],
      idealType: '打扮精致、头发保养得好的小姐姐，千万别是短发——浪费他的手艺',
      flaws: '不管聊什么最后都能绕回推销会员卡；八卦之魂熊熊燃烧，对方说什么他都能联想到店里客人的故事',
      datingResistance: '觉得相亲是拓展客源的绝佳渠道，来都来了不办张卡吗',
      height: 174, education: '专科 | 美发学院（但觉得自己比大学生有品味）', hometown: '安徽合肥', city: '上海静安',
      income: '月入1.5-4w（看旺季淡季和会员卡业绩）',
      hobbies: ['研究发型趋势', '看时尚杂志', '自拍', '逛美妆店'],
      lifestyle: '不抽烟，偶尔喝酒，发型每周换一个造型，护肤品比女朋友多',
      family: '有两个姐姐都已婚，父母在老家务农，靠自己打拼',
      partnerReq: '打扮精致，头发要好（这是基本条件），愿意让他给她做造型',
    },
    female: {
      name: 'Vicky', age: 25, avatarId: 31, extraTags: ['大嗓门'],
      idealType: '舍得给女朋友花钱做头发的男人，审美在线，别穿得像个程序员',
      flaws: '嗓门大到全场都能听见；忍不住点评对方的发型和穿搭，毫不留情面',
      datingResistance: '最近店里业绩不好，来相亲散散心，顺便看看能不能发展个客户',
      height: 163, education: '专科 | 某美容美发学院', hometown: '河南驻马店', city: '杭州',
      income: '月入1-3w（底薪+提成+会员卡提成）',
      hobbies: ['研究发色搭配', '看美妆博主视频', '八卦', '唱KTV'],
      lifestyle: '不抽烟偶尔喝酒，嗓门大，热情得让你招架不住',
      family: '有一个弟弟在读大学，父母务农，她是家里经济支柱之一',
      partnerReq: '审美在线，愿意花钱打扮自己，别穿得像个码农（说的就是你格子衫）',
    },
  },
  // 12. Doctor (洁癖)
  {
    id: 'doctor', job: '外科医生', bio: '每天洗手50次，把约会对象当标本。',
    tags: ['洁癖', '强迫症', '性冷淡'],
    landmines: [
      '对方表现出不卫生的行为——打喷嚏不遮、用手拿食物、舔手指等',
      '说"医生不就是开药的吗"或质疑医学权威/相信偏方',
      '对方身上有很重的香水味/烟味，或穿着邋遢',
    ],
    primaryZodiac: '水瓶座', primaryMbti: 'ISTJ',
    secondaryZodiac: '狮子座', secondaryMbti: 'ESFP',
    male: {
      name: 'Dr. Liu', age: 35, avatarId: 12, extraTags: ['理智'],
      idealType: '干净整洁、生活习惯规律的女性，不化浓妆，指甲剪短，最好也是医护人员',
      flaws: '把约会当门诊，用多巴胺和催产素解释一切感情；洁癖到不敢和人握手',
      datingResistance: '从流行病学角度分析，相亲的感染风险和成功率都不理想',
      height: 179, education: '博士 | 协和医学院', hometown: '山东潍坊', city: '北京东城',
      income: '年薪40w+（含奖金），稳定有五险一金',
      hobbies: ['看医学期刊', '跑步（保持身体健康是职业素养）', '做饭（精确计量每种调料）', '养多肉植物'],
      lifestyle: '绝对不抽烟不喝酒，洁癖严重，家里可以做手术那种干净程度',
      family: '有一个妹妹做护士，父母都是教师，家庭传统和睦',
      partnerReq: '干净整洁生活规律，最好医护行业能互相理解作息，不化浓妆',
    },
    female: {
      name: 'Dr. Zhao', age: 33, avatarId: 32, extraTags: ['高冷'],
      idealType: '理性克制、尊重个人空间的男性，不会动不动就要牵手拥抱那种',
      flaws: '情感表达能力接近于零；和人说话像在下医嘱，冷到对方怀疑人生',
      datingResistance: '每天手术排到半夜，相亲纯粹是为了让爸妈别再念叨',
      height: 165, education: '博士 | 复旦医学院', hometown: '江苏无锡', city: '上海浦东',
      income: '年薪35w+，稳定有保障',
      hobbies: ['看文献', '跑步', '弹钢琴（少数感性时刻）', '整理房间'],
      lifestyle: '不抽烟不喝酒，洁癖，作息取决于手术排班',
      family: '独生女，父亲大学教授，母亲退休公务员，家教严格',
      partnerReq: '理性克制，尊重个人空间，有稳定事业，不要动不动就要黏在一起',
    },
  },
  // 13. Master (神棍)
  {
    id: 'master', job: '国学大师', bio: '满口易经八卦，其实想看手相。',
    tags: ['神棍', '迷信', '忽悠'],
    landmines: [
      '说"这些都是封建迷信/伪科学"或用科学常识当面打脸',
      '对方完全不信命理还振振有词地反驳，让大师下不来台',
      '说"你这不就是江湖骗子吗"或要求大师当场算一卦验证',
    ],
    primaryZodiac: '射手座', primaryMbti: 'ENTP',
    secondaryZodiac: '处女座', secondaryMbti: 'ISTJ',
    male: {
      name: 'Master Chen', age: 50, avatarId: 13, extraTags: ['油腻'],
      idealType: '面相好、旺夫的年轻女性，八字要和他互补，手相要有财运线',
      flaws: '满嘴玄学但其实是个骗子；以看手相为借口碰人家手；油腻到令人窒息',
      datingResistance: '掐指一算今日不宜相亲，但既然来了不如看看对方面相值多少钱',
      height: 170, education: '不详（自称师从某隐世高人）', hometown: '湖南湘西', city: '深圳（有钱人多，生意好做）',
      income: '月入5-15w（看客户质量），收入来源你最好别细问',
      hobbies: ['研究易经', '看手相面相', '喝茶', '下棋'],
      lifestyle: '抽烟（说是点香观相），喝茶成瘾，作息看黄历',
      family: '离异，有一个成年儿子在某金融公司，前妻不愿提',
      partnerReq: '面相好、旺夫，年龄30-40最佳，八字合就行其他都好说',
    },
    female: {
      name: 'Madam Wu', age: 48, avatarId: 33, extraTags: ['大仙'],
      idealType: '命里带财、面善心诚的有缘人，最好信她那一套别总质疑',
      flaws: '三句话不离阴阳五行，正常人根本接不住；喜欢装神弄鬼吓唬人',
      datingResistance: '给自己算了一卦说今年有桃花劫，来相亲是为了验证准不准',
      height: 158, education: '高中（自称祖传手艺不需要学历）', hometown: '福建莆田', city: '广州',
      income: '月入3-10w（看风水布局+算命+卖符），旺季更多',
      hobbies: ['看面相', '研究风水', '收集玉器', '看宫斗剧'],
      lifestyle: '不抽烟，喝功夫茶，初一十五吃素，每天烧香拜佛',
      family: '独身（说是命里注定先立业后成家），有一个侄子在身边帮忙',
      partnerReq: '命里带财、面善心诚，别太聪明（太聪明的不好忽悠），愿意跟她学易经',
    },
  },
  // 14. Gamer (暴躁)
  {
    id: 'gamer', job: '电竞选手', bio: '把约会当排位赛，手速极快情商极低。',
    tags: ['网瘾', '暴躁', '直肠子'],
    landmines: [
      '说"打游戏有什么用/玩物丧志"或把电竞和网瘾少年画等号',
      '打断对方正在进行的游戏或说"能不能放下手机"',
      '说"电竞选手退役了能干什么"或质疑电竞的前途',
    ],
    primaryZodiac: '白羊座', primaryMbti: 'ISTP',
    secondaryZodiac: '天秤座', secondaryMbti: 'ENFJ',
    male: {
      name: 'Jay', age: 22, avatarId: 14, extraTags: ['祖安人'],
      idealType: '能陪他双排的女生，段位至少钻石以上，别是那种玩不来还要带的',
      flaws: '情商为负数，说话跟祖安喷子一样不过脑子；约会中途可能掏手机打排位',
      datingResistance: '今天有比赛训练赛，来相亲纯粹因为队友说"你该碰碰草了"',
      height: 173, education: '本科 | 某大学（挂了好几科在补考）', hometown: '湖北武汉', city: '上海（电竞公司在这）',
      income: '月入1-5w（比赛奖金+直播打赏，波动大）',
      hobbies: ['打LOL/VALORANT', '直播', '看比赛回放', '吃泡面'],
      lifestyle: '不抽烟，喝大量咖啡和功能饮料，昼夜颠倒，正餐靠外卖',
      family: '有一个姐姐在国企，父母开小店，对他打游戏又爱又恨',
      partnerReq: '会打游戏段位不能太低，别在他打排位时说话，能接受他的作息',
    },
    female: {
      name: 'Viper', age: 21, avatarId: 34, extraTags: ['带妹'],
      idealType: '游戏里能carry她的大神，现实里也要又帅又会照顾人',
      flaws: '赢了嘲讽输了暴躁，情绪波动比游戏里的血条还剧烈；注意力只能维持15秒',
      datingResistance: '新赛季刚开，冲分要紧，相亲要是无聊就当AFK挂机了',
      height: 160, education: '本科在读 | 某大学电竞管理专业', hometown: '浙江杭州', city: '杭州',
      income: '月入几千-1w（直播打赏+代练佣金）',
      hobbies: ['打游戏', '看电竞比赛', '吃零食', '和粉丝互动'],
      lifestyle: '不抽烟，嗜甜食和奶茶，作息全看有没有比赛',
      family: '独生女，父母做生意，家境不错，爸妈觉得打游戏也是正经事',
      partnerReq: '游戏打得好能carry她，长得帅，别是那种说教她别打游戏的',
    },
  },
  // 15. Sales (势利)
  {
    id: 'sales', job: '柜员', bio: '看人下菜碟，只认Logo不认人。',
    tags: ['拜金', '势利眼', '职业假笑'],
    landmines: [
      '穿着明显是地摊货/快时尚品牌或说"我不看牌子"',
      '说"奢侈品都是智商税"或表示对名牌完全无感',
      '约会AA制或点最便宜的菜，暴露"低消费力"',
    ],
    primaryZodiac: '狮子座', primaryMbti: 'ESFJ',
    secondaryZodiac: '巨蟹座', secondaryMbti: 'INFJ',
    male: {
      name: 'Kevin', age: 24, avatarId: 35, extraTags: ['柜哥'],
      idealType: '白富美客户那种类型，拎爱马仕穿Chanel，一看就是VIP级别',
      flaws: '三秒内扫完对方全身穿搭并估价；对"穷人"态度一百八十度大转弯',
      datingResistance: '来看看相亲对象的消费水平，万一是个大客户呢',
      height: 181, education: '本科 | 某大学服装设计', hometown: '上海本地', city: '上海',
      income: '月入1-3w（底薪+提成，VIP客户多的月份能翻倍）',
      hobbies: ['逛奢侈品店（蹭打折）', '研究时尚趋势', '健身保持身材', '拍穿搭'],
      lifestyle: '不抽烟，偶尔品酒（配合客户），穿着永远一丝不苟',
      family: '独生子，父母做小生意，家境普通但他穿得不像',
      partnerReq: '有品味，舍得在自己身上花钱，别穿地摊货出来丢人',
    },
    female: {
      name: 'Coco', age: 25, avatarId: 15, extraTags: ['柜姐'],
      idealType: '出手大方的成功男士，约会直接带她去买包那种，不用她开口',
      flaws: '以貌取人到骨子里，见人先看鞋表包再看脸；嘴上说的和心里想的永远不一样',
      datingResistance: '周末本来想逛街的，要是对方穿Zara来相亲她就直接走人',
      height: 169, education: '大专 | 某时尚学院', hometown: '江苏南通', city: '上海',
      income: '月入1.5-5w（底薪+VIP客户提成），好月份能到8w',
      hobbies: ['研究奢侈品新款', '逛商场', '做美甲', '和闺蜜下午茶'],
      lifestyle: '不抽烟，社交性喝酒，穿着品味>一切，包治百病',
      family: '有一个哥哥在工厂，父母务农，靠自己在上海站稳脚跟',
      partnerReq: '出手大方穿着得体，至少拎得出手的品牌，约会别AA',
    },
  },
  // 16. Idol (病娇)
  {
    id: 'idol', job: '地下偶像', bio: '如果不推我就诅咒你哦！',
    tags: ['腹黑', '二次元', '营业'],
    landmines: [
      '提到喜欢其他偶像/明星或说"我推的是XXX"——忠诚度为零直接黑化',
      '表现出对对方不感兴趣或说"你好普通"——自尊心碎裂',
      '说"地下偶像就是没出道的吧"或暗示对方不红/过气',
    ],
    primaryZodiac: '天蝎座', primaryMbti: 'ENFP',
    secondaryZodiac: '摩羯座', secondaryMbti: 'ISTJ',
    male: {
      name: 'Kira', age: 20, avatarId: 36, extraTags: ['病娇男'],
      idealType: '对他一心一意的专属粉丝，只推他一个人，不准看别的男生',
      flaws: '表面可爱实则占有欲爆表；一旦觉得被忽视就黑化，说话开始阴阳怪气',
      datingResistance: '来相亲是为了发展新粉丝，如果对方不推他就诅咒对方',
      height: 175, education: '高中（为了追梦放弃了高考）', hometown: '浙江温州', city: '东京/上海',
      income: '月入几百-几千（粉丝打赏+小演出），基本在赔钱做梦',
      hobbies: ['练舞', '录翻唱', '和粉丝互动', '追日本偶像（学习）'],
      lifestyle: '不抽烟不喝酒（偶像要保持好形象），护肤化妆一丝不苟',
      family: '有一个哥哥在家族企业，父母做生意很有钱，但不支持他当偶像',
      partnerReq: '只推他一个人，24小时给他点赞评论，别提别的男生',
    },
    female: {
      name: 'Momo', age: 19, avatarId: 16, extraTags: ['病娇女'],
      idealType: '只看她一个人的忠犬系男友，24小时回消息，不准和别的女生说话',
      flaws: '甜到发齁的表面下藏着恐怖的独占欲；翻脸比翻书快，上一秒撒娇下一秒威胁',
      datingResistance: '粉丝不让她谈恋爱，但如果是真爱她可以不要所有粉丝（才怪）',
      height: 158, education: '高中毕业（决定全职做偶像）', hometown: '广东广州', city: '上海',
      income: '月入1k-5k（粉丝打赏+小舞台演出），基本是用爱发电',
      hobbies: ['练舞', '学唱歌', '在推特和粉丝互动', '看少女漫画'],
      lifestyle: '不抽烟不喝酒，早睡早起保持好皮肤，饮食控制体重',
      family: '独生女，父母在老家开餐馆，对她当偶像不太理解但在默默支持',
      partnerReq: '只看她一个人，24小时回消息，不准关注别的女生，长得好看加分',
    },
  },
  // 17. Teacher (说教)
  {
    id: 'teacher', job: '高中班主任', bio: '你这个态度，怎么跟家里人交代？',
    tags: ['说教', '严厉', '职业病'],
    landmines: [
      '对方表现出不尊重老师/教育行业，说"老师工资那么低还那么多假期"',
      '当面顶嘴、态度叛逆，像个不服管教的学生',
      '说"读书没用/学历不重要"或表现出反智倾向',
    ],
    primaryZodiac: '摩羯座', primaryMbti: 'ESTJ',
    secondaryZodiac: '射手座', secondaryMbti: 'ENFP',
    male: {
      name: 'Mr. Wang', age: 40, avatarId: 37, extraTags: ['老古板'],
      idealType: '温顺乖巧的贤内助，要像他最得意的课代表一样听话懂事',
      flaws: '职业病深入骨髓，开口就是教育口吻；把约会当家长会，不自觉地给对方打分',
      datingResistance: '年轻人太浮躁了，当年他带的班成绩那么好就是因为专注，恋爱影响学习',
      height: 175, education: '本科 | 某师范大学中文教育', hometown: '山东烟台', city: '北京',
      income: '年薪20w+（含课时费和辅导），寒暑假是优势',
      hobbies: ['看历史书', '练书法', '下象棋', '看CCTV纪录片'],
      lifestyle: '偶尔抽烟（背着学生），少喝酒，作息极规律',
      family: '有一个已婚姐姐，父母退休教师，一家子教育世家',
      partnerReq: '温顺贤惠，尊师重道，学历本科以上，最好是做老师或护士的',
    },
    female: {
      name: 'Teacher Zhang', age: 38, avatarId: 17, extraTags: ['灭绝师太'],
      idealType: '尊师重道、上进的男性，最好学历在她之上，否则她看不起',
      flaws: '纠正别人是本能反应，从坐姿到说话方式都要管；严厉到让人以为在挨批评',
      datingResistance: '这些年送走了无数届学生，觉得没人能通过她设的考核标准',
      height: 162, education: '硕士 | 某师范大学英语教育', hometown: '河北石家庄', city: '北京',
      income: '年薪18w+（含寒暑假），有稳定编制',
      hobbies: ['看教育类书籍', '练瑜伽', '追英美剧（说是练听力）', '种花'],
      lifestyle: '不抽烟不喝酒，作息极规律，晚上10点前必须睡觉',
      family: '独生女，父亲退休军人，母亲退休会计，家教极严',
      partnerReq: '学历硕士以上（必须比她高），有稳定工作，尊重教师职业，上进有担当',
    },
  },
  // 18. Eco (道德绑架)
  {
    id: 'eco', job: '环保主义者', bio: '怎么可以吃兔兔？地球在哭泣！',
    tags: ['白左', '极端', '道德绑架'],
    landmines: [
      '当面吃肉还说"好香"，或表示"我就是无肉不欢"',
      '用塑料袋/一次性筷子/瓶装水等不环保行为并毫不在意',
      '说"环保是矫情/作秀"或"一个人能改变什么"',
    ],
    primaryZodiac: '水瓶座', primaryMbti: 'INFJ',
    secondaryZodiac: '金牛座', secondaryMbti: 'ESTP',
    male: {
      name: 'River', age: 28, avatarId: 38, extraTags: ['素食男'],
      idealType: '纯素食主义女神，自带环保袋，碳中和生活方式，最好不化妆（动物实验）',
      flaws: '道德绑架成瘾，对方喝瓶装水都要被教育；自以为是到让人窒息',
      datingResistance: '来相亲之前先确认餐厅有没有素食选项，有塑料吸管就不去',
      height: 178, education: '硕士 | 某大学环境科学', hometown: '云南昆明', city: '北京/大理（两地漂）',
      income: '月入8k-2w（NGO+自媒体+讲座），够活但不多',
      hobbies: ['徒步净山', '种菜', '做堆肥', '写环保公众号'],
      lifestyle: '纯素食，不抽烟不喝酒，自带餐具和环保袋，拒绝一切一次性用品',
      family: '有一个弟弟在体制内，父母退休公务员，觉得他太理想主义',
      partnerReq: '认同环保理念，最好素食或愿意减少肉食，不用一次性筷子是最低标准',
    },
    female: {
      name: 'Lin', age: 27, avatarId: 18, extraTags: ['圣母'],
      idealType: '有社会责任感的暖男，最好和她一起做公益，拯救世界',
      flaws: '圣母心泛滥到管不住自己的嘴；见不得任何人过得比动物好',
      datingResistance: '约会产生的碳排放让她有负罪感，不如在家种多肉',
      height: 163, education: '本科 | 某大学社会学', hometown: '四川成都', city: '成都',
      income: '月入6k-1.5w（公益组织+兼职翻译），不追求物质',
      hobbies: ['做义工', '照顾流浪动物', '画画', '写环保日记'],
      lifestyle: '素食主义，不抽烟不喝酒，拒绝快时尚只穿二手衣服',
      family: '有一个姐姐在国企，父母退休教师，家庭和睦但不理解她为什么挣那么少',
      partnerReq: '有社会责任感的暖男，不嫌她挣得少，别是那种只认钱的人',
    },
  },
  // 19. Start-up Founder (画饼大王)
  {
    id: 'founder', job: '连续创业者', bio: '我们正在改变世界，目前估值一个亿。',
    tags: ['画饼', '焦虑', '长期主义'],
    landmines: [
      '说"创业十个九个死"或问"你融到钱了吗/公司盈利了吗"',
      '表示追求稳定/铁饭碗，说"还是考公好"——价值观直接冲突',
      '说"你这个idea我见过一百个了"或对项目表现出敷衍和不屑',
    ],
    primaryZodiac: '白羊座', primaryMbti: 'ENTP',
    secondaryZodiac: '巨蟹座', secondaryMbti: 'ISFJ',
    male: {
      name: 'Elon', age: 31, avatarId: 39, extraTags: ['梦想家'],
      idealType: '愿意和他一起吃苦的梦想合伙人，最好能免费当COO，等上市了给期权',
      flaws: '永远在画饼但一个都兑现不了；把约会当路演，三分钟内必提自己的项目',
      datingResistance: '太忙了没时间谈恋爱（其实是太穷了请不起客），来是投资人介绍的',
      height: 180, education: '本科 | 某985（MBA没读完就创业了）', hometown: '福建泉州', city: '深圳南山（创业圣地）',
      income: '名义年薪100w（公司还在烧钱，实际到手看融资情况）',
      hobbies: ['看创业书', '参加路演', 'networking', '凌晨发朋友圈鸡汤'],
      lifestyle: '不抽烟，社交性喝酒（投资人局），一周工作7天，睡在公司是常态',
      family: '有一个弟弟继承家族工厂，父母做传统制造业，能给他兜底',
      partnerReq: '独立自强的事业型女性，别问他什么时候有空，要能理解创业者的孤独',
    },
    female: {
      name: 'Elizabeth', age: 29, avatarId: 40, extraTags: ['女强人'],
      idealType: '有资源的成熟男性，最好是投资人或者有渠道的，能帮她项目的那种',
      flaws: '分不清商务社交和约会的区别；焦虑到凌晨三点还在发工作消息',
      datingResistance: '来相亲是因为"social是创业者的必修课"，顺便看看有没有天使投资人',
      height: 167, education: '硕士 | 某985 + 海外MBA', hometown: '浙江宁波', city: '杭州/深圳（看项目在哪）',
      income: '名义年薪80w（实际到手看项目进度），账户上的数字经常是负的',
      hobbies: ['看商业案例', '参加创投圈饭局', '跑步（说是冥想）', '写BP'],
      lifestyle: '不抽烟，应酬喝酒，凌晨三点还在回工作消息是日常',
      family: '独生女，父母经商，家境不错，是她创业的启动资金来源',
      partnerReq: '有资源有人脉，成熟有格局，别只会打工，最好能互相赋能',
    },
  },
  // 20. Barista (咖啡成瘾)
  {
    id: 'barista', job: '精品咖啡师', bio: '这杯豆子产自巴拿马海拔2000米...',
    tags: ['文青', '挑剔', '强迫症'],
    landmines: [
      '说"咖啡不都一样吗"或说自己喝速溶/瑞幸就够了',
      '往精品咖啡里加糖加奶，或说"我只喝甜的"',
      '说"咖啡师不就是泡咖啡的服务员吗"——把手艺人贬低成服务业',
    ],
    primaryZodiac: '天秤座', primaryMbti: 'ISFP',
    secondaryZodiac: '白羊座', secondaryMbti: 'ESTP',
    male: {
      name: 'Sora', age: 25, avatarId: 41, extraTags: ['手冲党'],
      idealType: '懂品味的文艺女生，能分辨出他手冲咖啡的风味层次，千万别说"来杯美式"',
      flaws: '对咖啡以外的事情完全不care；品味鄙视链严重，喝速溶的在他眼里不配做人',
      datingResistance: '下午三点要做杯测，四点要烘豆子，真的没有时间来相亲',
      height: 174, education: '本科 | 某大学食品科学（自称在学校学的不如在咖啡馆多）', hometown: '北京', city: '上海',
      income: '月入1-2w（打比赛拿过奖之后稍好，但不稳定）',
      hobbies: ['杯测', '烘豆', '逛咖啡馆', '听黑胶唱片'],
      lifestyle: '不抽烟（会影响味觉），少量喝酒，每天必喝5杯以上咖啡',
      family: '独生子，父母都是大学教师，支持他追求热爱但偶尔嘀咕收入',
      partnerReq: '有品味，能分辨咖啡风味，别是那种只喝奶茶的，审美在线',
    },
    female: {
      name: 'Miya', age: 24, avatarId: 42, extraTags: ['拉花控'],
      idealType: '有生活美学的男生，能欣赏她拉花艺术的那种，别是只喝白开水的直男',
      flaws: '对细节挑剔到病态，水温差1度都要重来；无法容忍任何"粗糙"的生活方式',
      datingResistance: '相亲地点要是不是精品咖啡馆她就拒绝出席',
      height: 162, education: '本科 | 某大学设计学院', hometown: '江苏南京', city: '上海',
      income: '月入8k-1.5w（咖啡师+偶尔接设计私活）',
      hobbies: ['拉花', '手冲', '逛独立书店', '摄影'],
      lifestyle: '不抽烟，偶尔品酒，生活美学主义者，餐具比衣服多',
      family: '有一个哥哥在国企，父母退休公务员，支持她做喜欢的事',
      partnerReq: '有审美的文艺男生，能欣赏她的手艺，别是那种只喝速溶还嫌她矫情的',
    },
  },
  // 21. Civil Servant (考公党)
  {
    id: 'civil', job: '体制内人员', bio: '宇宙的尽头是编制，稳定压倒一切。',
    tags: ['保守', '稳重', '官腔'],
    landmines: [
      '说"体制内就是混吃等死/养老"或暗示公务员不创造价值',
      '对方是自由职业/创业者还炫耀自己多自由——戳中体制内人的痛处',
      '说"你们工资那么低图什么"或质疑体制内的意义',
    ],
    primaryZodiac: '巨蟹座', primaryMbti: 'ISTJ',
    secondaryZodiac: '射手座', secondaryMbti: 'ENTP',
    male: {
      name: 'Mr. Liu', age: 28, avatarId: 43, extraTags: ['厅局风'],
      idealType: '同样有编制的女孩，最好在同一个系统内，双职工分房方便',
      flaws: '说话像在念公文，毫无情趣可言；任何事都要走流程，连约会都要提前报备',
      datingResistance: '组织上安排的相亲活动，不来影响考核评优',
      height: 176, education: '硕士 | 某985行政管理', hometown: '安徽合肥', city: '北京',
      income: '年薪20w+（阳光工资，但有公积金和各种福利），有购房资格',
      hobbies: ['看新闻联播', '跑步', '看公考备考经验帖', '打乒乓球'],
      lifestyle: '不抽烟少喝酒（单位活动除外），作息极规律，准时上下班',
      family: '独生子，父母都在体制内，家庭和睦稳定',
      partnerReq: '最好也有编制（双编制分房快），温柔贤惠别太折腾，追求稳定的',
    },
    female: {
      name: 'Ms. Chen', age: 27, avatarId: 44, extraTags: ['上岸'],
      idealType: '稳定体面的体制内男性，公务员优先，事业编也凑合',
      flaws: '极度风险厌恶，听到"创业"两个字就跑；把一切量化成编制、房子、户口',
      datingResistance: '考公上岸已经耗尽全部精力了，相亲是爸妈给安排的任务',
      height: 163, education: '硕士 | 某211法学', hometown: '江西南昌', city: '北京',
      income: '年薪18w+（有编制），五险一金齐全',
      hobbies: ['练瑜伽', '看书', '追剧', '烘焙'],
      lifestyle: '不抽烟不喝酒，作息规律，周末逛公园',
      family: '有一个姐姐在老家当老师，父母在老家务农，她是全家骄傲',
      partnerReq: '有稳定编制（公务员优先），有房或有购房能力，踏实靠谱别整虚的',
    },
  },
  // 22. Crypto Bro (币圈)
  {
    id: 'crypto', job: 'Web3布道者', bio: 'To the Moon! 懂的都懂。',
    tags: ['赌徒', '暴发户', '韭菜'],
    landmines: [
      '说"比特币是骗局/庞氏骗局"或"加密货币迟早归零"',
      '说"你这就是赌博"或把Web3和传销相提并论',
      '问"你到底赚了多少钱"并表现出对方在亏钱的同情',
    ],
    primaryZodiac: '射手座', primaryMbti: 'ESTP',
    secondaryZodiac: '巨蟹座', secondaryMbti: 'ISFJ',
    male: {
      name: 'Satoshi', age: 26, avatarId: 45, extraTags: ['去中心化'],
      idealType: '信仰Web3的crypto girl，或者至少愿意听他讲区块链讲到天亮的',
      flaws: '情绪完全跟着币价走，涨了请客吃海底捞跌了连外卖都不敢点；把所有话题都能扯到币圈',
      datingResistance: '行情波动太大没心思相亲，除非对方也是diamond hands',
      height: 178, education: '本科 | 某985计算机（辍学创业——做Web3）', hometown: '广东深圳', city: '新加坡/深圳（说是税收优化）',
      income: '未知（自称资产7位数但也可能是负数，全在链上）',
      hobbies: ['盯盘', '逛推特Crypto圈', '参加Web3 hackathon', '写白皮书'],
      lifestyle: '不抽烟，喝功能饮料，作息跟着美股和币市走，UTC时间生活',
      family: '独生子，父母在深圳做IT，家境中上，不太理解他的事业',
      partnerReq: '信仰Web3，至少能听懂区块链术语，别把crypto当骗局',
    },
    female: {
      name: 'Crypto Kate', age: 25, avatarId: 46, extraTags: ['矿工'],
      idealType: '懂区块链的技术大佬，或者干脆就是鲸鱼账户持有者',
      flaws: '把所有关系都类比成去中心化治理；赚了就嘚瑟亏了就消失',
      datingResistance: '正在盯盘呢，BTC要是突破新高她就不来了',
      height: 166, education: '本科 | 某大学金融（后自学区块链）', hometown: '湖南长沙', city: '上海/新加坡（看项目）',
      income: '链上资产未知，可能暴富也可能归零',
      hobbies: ['研究DeFi协议', '参加Crypto大会', '画NFT', '做空做多'],
      lifestyle: '不抽烟，喝咖啡续命，24小时在线（区块链永不眠）',
      family: '有一个弟弟在读高中，父母做小生意，觉得她在搞传销',
      partnerReq: '最好是crypto native，至少愿意了解Web3，别一上来就说比特币是骗局',
    },
  },
  // 23. Art Curator (伪文青)
  {
    id: 'art', job: '独立策展人', bio: '你这种审美，我们很难有共同语言。',
    tags: ['高冷', '抽象', '不可一世'],
    landmines: [
      '说"看不懂的艺术就是在装"或"我觉得这画我也能画"',
      '表示喜欢大众流行文化（抖音、综艺、网红打卡）——审美鄙视链底端',
      '说"艺术又不能当饭吃"或问"策展人能赚多少钱"',
    ],
    primaryZodiac: '天秤座', primaryMbti: 'INTJ',
    secondaryZodiac: '白羊座', secondaryMbti: 'ESFP',
    male: {
      name: 'Vincent', age: 33, avatarId: 47, extraTags: ['艺术家'],
      idealType: '有独立审美且不随波逐流的灵魂，最好能听懂他讲德勒兹和后现代主义',
      flaws: '装逼装到自己都信了；看不起一切大众文化，但其实自己也没什么代表作',
      datingResistance: '觉得相亲是一种"媚俗"的社会规训，真正的灵魂相遇不需要中介',
      height: 182, education: '硕士 | 中央美院 + 柏林艺术大学交换', hometown: '北京', city: '北京798附近',
      income: '月入0.5-5w（策展费+艺术评论稿费，极不稳定）',
      hobbies: ['逛画廊', '看艺术电影', '读哲学', '写展评'],
      lifestyle: '抽烟（说是艺术家标配），喝红酒，作息不规律但充满仪式感',
      family: '独生子，父母是高知，支持他搞艺术但偶尔催婚',
      partnerReq: '有独立审美，不跟风网红打卡，能聊当代艺术和哲学',
    },
    female: {
      name: 'Frida', age: 31, avatarId: 48, extraTags: ['先锋'],
      idealType: '有才华的创作者，最好是那种穷但有趣的艺术家，不要商人',
      flaws: '审美洁癖到令人窒息，对方穿错颜色她都要皱眉；说话永远在掉书袋',
      datingResistance: '正在筹备个展，哪有时间约会，除非对方能给她灵感',
      height: 170, education: '硕士 | 某美院 + 伦敦艺术大学', hometown: '上海', city: '上海/柏林',
      income: '月入1-3w（策展+艺术教育+偶尔卖画），靠热爱续命',
      hobbies: ['创作装置艺术', '逛美术馆', '看独立电影', '写艺术评论'],
      lifestyle: '偶尔抽烟，喝红酒，生活充满仪式感但混乱',
      family: '独生女，父亲建筑师，母亲钢琴教师，文艺世家',
      partnerReq: '有才华的创作者，不要商人，能给她灵感，审美在线',
    },
  },
  // 24. Outdoor (露营狂)
  {
    id: 'outdoor', job: '户外领队', bio: '逃离城市，山系生活才是yyds。',
    tags: ['山系', '现充', '装备党'],
    landmines: [
      '说"我是宅家派/出门太累了"或表示完全不喜欢户外运动',
      '嘲笑露营装备贵或说"住帐篷有什么好的不如住酒店"',
      '说"爬山有什么意思"或对大自然表现出毫无兴趣',
    ],
    primaryZodiac: '双子座', primaryMbti: 'ESTP',
    secondaryZodiac: '巨蟹座', secondaryMbti: 'INFJ',
    male: {
      name: 'Rocky', age: 30, avatarId: 49, extraTags: ['驴友'],
      idealType: '能一起徒步露营的野性女孩，千万别是娇滴滴怕晒怕虫子的',
      flaws: '装备鄙视链严重，看你穿迪卡侬就觉得不配上山；室内待超过两小时就焦躁',
      datingResistance: '这周末有条新线路要探，来相亲不如去爬山',
      height: 182, education: '本科 | 某大学户外运动与休闲专业', hometown: '云南丽江', city: '成都（登山大本营）',
      income: '月入1-3w（带队费+装备代购，旺季能到5w）',
      hobbies: ['登山', '攀岩', '徒步', '露营'],
      lifestyle: '不抽烟不喝酒，身体是本钱，随时保持最佳体能状态',
      family: '有一个妹妹在昆明做导游，父母在老家开客栈',
      partnerReq: '能一起户外的女孩，别走两步就喊累，有一定体能基础',
    },
    female: {
      name: 'Sunny', age: 29, avatarId: 50, extraTags: ['飞盘媛'],
      idealType: '阳光运动型男生，最好有车能自驾去野外，还得愿意帮她搭帐篷',
      flaws: '朋友圈全是精修露营照，实际户外技能约等于零；装备花了十万用了两次',
      datingResistance: '约在室内相亲太无聊了，要约就约在野外，顺便出片',
      height: 166, education: '本科 | 某大学旅游管理', hometown: '四川成都', city: '成都',
      income: '月入1.5-3w（户外博主+带队+装备种草佣金）',
      hobbies: ['露营（出片）', '飞盘', '骑行', '拍户外大片'],
      lifestyle: '不抽烟，偶尔喝酒（营地篝火配红酒），防晒是比命还重要的事',
      family: '独生女，父母退休教师，觉得她整天在外面瞎跑不安全',
      partnerReq: '阳光运动型，有车能自驾，愿意当她的摄影师兼搬运工',
    },
  },
  // 25. Astrologer (占星师)
  {
    id: 'astro', job: '占星师', bio: '水逆期间不要和我说话。',
    tags: ['神婆', '宿命论', '情绪化'],
    landmines: [
      '说"星座是伪科学/巴纳姆效应"或用理性论据反驳占星',
      '不知道自己的上升星座/月亮星座，或说"这些有什么区别"',
      '说"我不信这些"还带着嘲讽的语气',
    ],
    primaryZodiac: '双鱼座', primaryMbti: 'INFJ',
    secondaryZodiac: '处女座', secondaryMbti: 'ESTJ',
    male: {
      name: 'Star', age: 26, avatarId: 51, extraTags: ['灵性'],
      idealType: '月亮星座和他合拍的女生，上升要在水象，金星最好在天秤',
      flaws: '一切行为都用星盘解释，不认星座的人在他眼里都是异端；情绪化到让人捉摸不定',
      datingResistance: '最近水星逆行，不适合开始新关系，等水逆过了再说',
      height: 176, education: '本科 | 某大学心理学（觉得占星比心理学更准）', hometown: '浙江杭州', city: '杭州',
      income: '月入1-3w（占星咨询+课程+知识付费），看当月客户量',
      hobbies: ['画星盘', '研究行星运行', '塔罗', '冥想'],
      lifestyle: '不抽烟不喝酒，生活节奏跟着月相走，新月许愿满月感恩',
      family: '有一个姐姐在银行，父母经商，对他的职业一言难尽',
      partnerReq: '月亮星座合拍是底线，上升在水象星座优先，金星不能在摩羯',
    },
    female: {
      name: 'Luna', age: 25, avatarId: 52, extraTags: ['女巫'],
      idealType: '星盘完美匹配的灵魂伴侣，日月合相、金火六合，缺一不可',
      flaws: '开口先问对方星座和上升，不合就直接判死刑；什么坏事都赖水逆',
      datingResistance: '今天金星冲土星，不适合相亲，但来都来了先看看对方星盘',
      height: 161, education: '本科 | 某大学中文系（后来自学占星）', hometown: '江苏苏州', city: '上海',
      income: '月入8k-2w（线上占星咨询+小红书接广告）',
      hobbies: ['看星盘', '收集水晶', '做月亮仪式', '写占星专栏'],
      lifestyle: '不抽烟，水逆期间不出门不签合同，随身带水晶辟邪',
      family: '独生女，父母在老家做生意，觉得她信这些不靠谱',
      partnerReq: '星盘匹配是第一位（日月合相优先），不接受不信占星的人',
    },
  },
  // 26. Model (模特)
  {
    id: 'model', job: '超模', bio: '经纪人说我这周只能吃草。',
    tags: ['花瓶', '节食', '高傲'],
    landmines: [
      '说"模特不就是走路的花瓶吗"或暗示模特没内涵',
      '在对方面前大吃碳水/高热量食物并劝对方"多吃点别减肥了"',
      '说对方"其实也没那么好看/很普通"——外貌是最大逆鳞',
    ],
    primaryZodiac: '狮子座', primaryMbti: 'ESFP',
    secondaryZodiac: '处女座', secondaryMbti: 'INTJ',
    male: {
      name: 'Gabriel', age: 23, avatarId: 53, extraTags: ['鲜肉'],
      idealType: '和他一样好看的女生，走在一起要像杂志封面，丑的不考虑',
      flaws: '自恋到一直在照镜子和自拍；恐碳水恐衰老，聊天全是卡路里和抗氧化',
      datingResistance: '最近在控体重备fashion week，约会吃饭怕破功',
      height: 188, education: '本科在读 | 某大学表演系', hometown: '新疆乌鲁木齐（混血）', city: '上海',
      income: '月入2-10w（看有没有大牌广告），收入极不稳定',
      hobbies: ['健身', '护肤', '拍大片', '研究时尚趋势'],
      lifestyle: '严格不抽烟不喝酒，控制饮食，每天睡够8小时保持状态',
      family: '有一个妹妹在读高中，父亲汉族做生意，母亲哈萨克族家庭主妇',
      partnerReq: '颜值在线（走在一起要好看），有品味，别比他丑太多',
    },
    female: {
      name: 'Naomi', age: 22, avatarId: 54, extraTags: ['高冷'],
      idealType: '比她高、比她有名、比她有钱的男人，至少也得是个品牌方',
      flaws: '高冷到让人以为在面试；恐碳水到极致，看到对方吃炸鸡就想走',
      datingResistance: '经纪人说不能公开恋爱影响商业价值，来看看算了',
      height: 176, education: '本科 | 某大学服装设计', hometown: '辽宁沈阳', city: '上海/巴黎（看走秀季）',
      income: '月入3-15w（看秀季和广告），巅峰月份很夸张',
      hobbies: ['走秀', '瑜伽', '看时尚杂志', '护肤'],
      lifestyle: '不抽烟不喝酒，严格控制饮食，碳水是敌人',
      family: '独生女，父亲做外贸，母亲全职太太，家境优渥',
      partnerReq: '比她高，比她有名或有钱，长相不能拉低两个人合影的水平',
    },
  },
  // 27. PhD (学术妲己/书呆子)
  {
    id: 'phd', job: '在读博士', bio: '这篇论文发不出来我就要延毕了...',
    tags: ['焦虑', '发际线', '老学究'],
    landmines: [
      '说"读博有什么用/博士出来还不是打工"或质疑学历价值',
      '提到对方的研究方向无聊/没有实际意义',
      '说"你怎么还没毕业"或提到延毕/发际线等敏感话题',
    ],
    primaryZodiac: '处女座', primaryMbti: 'INTJ',
    secondaryZodiac: '狮子座', secondaryMbti: 'ESFP',
    male: {
      name: 'Sheldon', age: 29, avatarId: 55, extraTags: ['书呆子'],
      idealType: '聪明但崇拜他的女生，最好是学妹，能帮他跑实验还觉得他好厉害',
      flaws: '智商优越感爆棚但生活能力为零；焦虑到脱发还嘴硬说"这是遗传"',
      datingResistance: '论文还没发出来哪有心思谈恋爱，导师知道了要骂人的',
      height: 175, education: '博士在读 | 某985物理系（第5年了还没毕业）', hometown: '湖北黄冈', city: '北京海淀（从宿舍到实验室就是全部活动半径）',
      income: '月入3k（博士补贴），偶尔有项目奖金能到5k',
      hobbies: ['看论文', '推公式', '和导师斗智斗勇', '偶尔打球'],
      lifestyle: '不抽烟不喝酒，三餐食堂，生活半径不超过3公里',
      family: '有一个姐姐在老家当医生，父母务农，全家指望他',
      partnerReq: '温柔崇拜他的就行，别问他什么时候毕业（会原地爆炸）',
    },
    female: {
      name: 'Amy', age: 28, avatarId: 56, extraTags: ['学术妲己'],
      idealType: '有学术追求但不和她卷的男生，最好是不同领域的，不构成竞争关系',
      flaws: '学术焦虑已经渗透到生活的每一个毛孔；无意识把约会当作学术会议提问环节',
      datingResistance: '这周要交稿，下周要答辩，下下周deadline，永远没有空窗期',
      height: 163, education: '博士在读 | 某985生物系（第4年，论文发了2篇还不够）', hometown: '安徽黄山', city: '上海（实验室动物比她的社交多）',
      income: '月入3.5k（博士补贴+助教），偶尔写综述赚点外快',
      hobbies: ['看文献', '泡实验室', '养实验用果蝇', '偶尔看看学术八卦'],
      lifestyle: '不抽烟不喝酒，咖啡续命，作息取决于实验进度',
      family: '独生女，父亲中学教师，母亲会计，家境普通',
      partnerReq: '有学术追求但不和她卷，不同领域避免竞争，理解她忙的人',
    },
  },
  // 28. Pet Lover (猫奴狗奴)
  {
    id: 'pet', job: '宠物美容师', bio: '可以不爱我，但必须爱我的毛孩子。',
    tags: ['铲屎官', '爱心泛滥', '社恐'],
    landmines: [
      '说"养宠物又脏又费钱"或表示讨厌/害怕猫狗',
      '说"动物就是动物别拟人化"或不理解把宠物当家人的感情',
      '提到虐待动物的新闻还表现出无所谓的态度',
    ],
    primaryZodiac: '巨蟹座', primaryMbti: 'ISFJ',
    secondaryZodiac: '天蝎座', secondaryMbti: 'ENTP',
    male: {
      name: 'Paw', age: 27, avatarId: 57, extraTags: ['猫奴'],
      idealType: '猫系女生——慵懒、独立、偶尔黏人，最重要是爱猫',
      flaws: '对人社恐但对动物热情百倍；三句话不离"我家主子"，手机相册全是猫片',
      datingResistance: '家里五只猫没人照顾，而且猫主子不批准他出门约会',
      height: 172, education: '专科 | 宠物美容学院（后来自学进阶）', hometown: '山东临沂', city: '北京朝阳',
      income: '月入1.5-3w（宠物美容+上门服务），旺季更多',
      hobbies: ['撸猫', '研究猫粮', '拍猫咪视频', '逛宠物用品店'],
      lifestyle: '不抽烟不喝酒（怕猫闻了不舒服），家里5只猫是真正的老板',
      family: '有一个弟弟在老家，父母务农，对他的职业不太理解',
      partnerReq: '必须爱猫（这是底线），不过敏，能接受家里5只猫',
    },
    female: {
      name: 'Kitty', age: 26, avatarId: 58, extraTags: ['狗奴'],
      idealType: '大金毛类型的暖男——忠诚、温暖、喜欢黏人，当然也要爱狗',
      flaws: '爱心泛滥到把所有感情都给了毛孩子；约会时全程刷宠物视频，对方说什么都心不在焉',
      datingResistance: '我家毛孩子才是真爱，人类太复杂了不如狗子单纯',
      height: 160, education: '本科 | 某大学动物科学', hometown: '河南洛阳', city: '成都',
      income: '月入1-2.5w（宠物美容+宠物博主+偶尔接寄养）',
      hobbies: ['遛狗', '做狗狗零食', '拍宠物视频', '参加流浪动物救助'],
      lifestyle: '不抽烟不喝酒，每天5:30起床遛狗，生活围着毛孩子转',
      family: '有一个哥哥在体制内，父母退休工人，家里也养了3只狗',
      partnerReq: '必须爱狗（不爱狗的免谈），有爱心，能接受她把狗子当孩子养',
    },
  }
];

// =============================================================================
// Weighted personality selection (seeded — deterministic per character ID)
// =============================================================================

/**
 * Selects zodiac & mbti for a character using weighted probability:
 *   50% → primary (the "expected" match for this archetype)
 *   25% → secondary (an interesting contrast / 反差感)
 *   25% → random from all remaining zodiac/mbti combos
 *
 * Uses seeded random so the result is deterministic per character ID.
 * Male and female of the same archetype may get DIFFERENT traits,
 * naturally creating variety within one archetype.
 */
function selectPersonality(
  charId: string,
  archetype: ArchetypeConfig,
): { zodiac: Zodiac; mbti: MBTI } {
  const roll = seededRandom(charId); // 0.000 – 0.999

  if (roll < 0.50) {
    // 50%: primary
    return { zodiac: archetype.primaryZodiac, mbti: archetype.primaryMbti };
  } else if (roll < 0.75) {
    // 25%: secondary (contrast)
    return { zodiac: archetype.secondaryZodiac, mbti: archetype.secondaryMbti };
  } else {
    // 25%: random from full pool (exclude primary & secondary to guarantee surprise)
    const remainingZodiacs = ALL_ZODIACS.filter(
      z => z !== archetype.primaryZodiac && z !== archetype.secondaryZodiac,
    );
    const remainingMbtis = ALL_MBTIS.filter(
      m => m !== archetype.primaryMbti && m !== archetype.secondaryMbti,
    );
    return {
      zodiac: seededPick(charId + '_z', remainingZodiacs),
      mbti: seededPick(charId + '_m', remainingMbtis),
    };
  }
}

// Helper to generate a character object
function createCharacter(
  archetype: ArchetypeConfig, 
  gender: 'male' | 'female'
): Character {
  const config = gender === 'male' ? archetype.male : archetype.female;
  const charId = `${archetype.id}_${gender.charAt(0)}`;
  const { zodiac, mbti } = selectPersonality(charId, archetype);
  
  return {
    id: charId, // e.g. banker_m
    name: config.name,
    age: config.age,
    gender: gender,
    job: archetype.job,
    bio: config.bioOverride || archetype.bio,
    tags: [...archetype.tags, ...(config.extraTags || [])],
    avatar: `https://picsum.photos/seed/${config.avatarId}/200/200`,
    idealType: config.idealType,
    flaws: config.flaws,
    datingResistance: config.datingResistance,
    landmines: archetype.landmines,
    zodiac,
    mbti,
    profile: {
      height: config.height,
      education: config.education,
      hometown: config.hometown,
      city: config.city,
      income: config.income,
      hobbies: config.hobbies,
      lifestyle: config.lifestyle,
      family: config.family,
      partnerReq: config.partnerReq,
    },
  };
}

/**
 * Generated Character Roster
 * Contains both Male and Female versions of each archetype.
 */
export const CHARACTERS: Character[] = ARCHETYPES.flatMap(archetype => [
  createCharacter(archetype, 'male'),
  createCharacter(archetype, 'female'),
]);

/** Look up a character by ID. Throws if not found. */
export function getCharacterById(id: string): Character {
  const char = CHARACTERS.find((c) => c.id === id);
  if (!char) throw new Error(`Unknown character ID: ${id}`);
  return char;
}
